import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { ScenarioActions } from './scenario.actions';
import {
  SCENARIO_STATE_SERVICE_INJECTION_TOKEN,
  ScenarioStateService,
} from '../../../core/services/api/scenario-state-service.interface';
import {
  selectFilteredScenariosWithCurrentPage,
  selectScenarioFilter,
  selectScenarioHistoryFilter,
} from './scenario.selectors';

@Injectable()
export class ScenarioEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private scenarioStateService = inject<ScenarioStateService>(
    SCENARIO_STATE_SERVICE_INJECTION_TOKEN,
  );

  public updateScenarioFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.public.updateScenarioFilter),
      map(() => ScenarioActions.private.loadScenariosWithFilters()),
    ),
  );

  public loadAllScenarios$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.public.loadAllScenarios),
      map(() => ScenarioActions.private.loadScenariosWithFilters()),
    ),
  );

  public loadScenariosWithFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.private.loadScenariosWithFilters),
      withLatestFrom(this.store.select(selectScenarioFilter)),
      switchMap(([_action, _filter]) =>
        this.scenarioStateService.getScenarios().pipe(
          map((scenariosResponseDto) =>
            ScenarioActions.private.loadScenariosSuccess({
              scenariosResponseDto,
            }),
          ),
          catchError((error) =>
            of(ScenarioActions.private.loadScenariosFailure({ error })),
          ),
        ),
      ),
    ),
  );

  public loadScenarioHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.public.loadScenarioHistory),
      withLatestFrom(
        this.store.select(selectFilteredScenariosWithCurrentPage),
        this.store.select(selectScenarioHistoryFilter),
      ),
      switchMap(([_action, scenarios, historyFilter]) => {
        const scenarioId = scenarios[0]?.id;
        if (!scenarioId) {
          return of(
            ScenarioActions.private.loadScenarioHistorySuccess({
              historyResponseDto: { histories: [], totalCount: 0 },
            }),
          );
        }

        return this.scenarioStateService.getScenarioHistory(historyFilter).pipe(
          map((historyResponseDto) =>
            ScenarioActions.private.loadScenarioHistorySuccess({
              historyResponseDto,
            }),
          ),
          catchError((error) =>
            of(ScenarioActions.private.loadScenarioHistoryFailure({ error })),
          ),
        );
      }),
    ),
  );

  public runScenario$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.public.runScenario),
      switchMap(({ id }) =>
        this.scenarioStateService.runScenario(id).pipe(
          map((responseDto) =>
            ScenarioActions.private.runScenarioSuccess({ id, responseDto }),
          ),
          catchError((error) =>
            of(ScenarioActions.private.runScenarioFailure({ id, error })),
          ),
        ),
      ),
    ),
  );

  public stopScenario$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScenarioActions.public.stopScenario),
      switchMap(({ id }) =>
        this.scenarioStateService.stopScenario(id).pipe(
          map((responseDto) =>
            ScenarioActions.private.stopScenarioSuccess({ id, responseDto }),
          ),
          catchError((error) =>
            of(ScenarioActions.private.stopScenarioFailure({ id, error })),
          ),
        ),
      ),
    ),
  );

  // Reload history off the role-spanning websocket feed (not runScenario/stopScenario
  // HTTP success) so RM_DEMO_GROUP companions - who never see the HTTP response, only
  // the holder's tab does - get a refreshed history too. Accepted trade-off: server
  // pushes can be lost or delayed (see ws-demo-connector.service.ts), so a reload can
  // occasionally be missed.
  public reloadScenarioHistoryAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ScenarioActions.public.scenarioStartedFromWebSocket,
        ScenarioActions.public.scenarioStoppedFromWebSocket,
        ScenarioActions.private.loadScenariosSuccess,
      ),
      map(() => ScenarioActions.public.loadScenarioHistory()),
    ),
  );
}
